import { useState, useCallback, useMemo } from 'react'
import { StatusType, GuestRoomAssignStatusResponseType } from '@/models/room-manager/response/guest-room-assign-status'

// アサイン画面用のグループ化処理
export const useHoverGroupingForAssign = (guestRooms: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList'] | undefined) => {
  // ホバーした予約のcheckinId
  const [hoveredCheckinId, setHoveredCheckinId] = useState<string | null>(null)
  // ホバーした予約のreservationId
  const [hoveredReservationId, setHoveredReservationId] = useState<string | null>(null)

  // グループ化可能なcheckinIdとreservationIdの配列
  const groupableAssignIds = useMemo(() => {
    if (!guestRooms) return { checkinIds: new Set<string>(), reservationIds: new Set<string>() }

    const allStatuses = Object.values(guestRooms)
      .flatMap(floor => Object.values(floor))
      .flatMap(room => room.statuses)

    // 重複しているIDだけを抽出する関数
    const findDuplicateIds = (statuses: StatusType[], extractId: (status: StatusType) => string | undefined) => {
      const idCounts = new Map<string, number>()

      statuses.forEach(status => {
        const id = extractId(status)
        if (id) idCounts.set(id, (idCounts.get(id) ?? 0) + 1)
      })

      return new Set(
        Array.from(idCounts)
          .filter(([, count]) => count >= 2)
          .map(([id]) => id),
      )
    }

    return {
      checkinIds: findDuplicateIds(allStatuses, status => status.checkinId),
      reservationIds: findDuplicateIds(allStatuses, status => status.reservationId),
    }
  }, [guestRooms])

  // ハイライト判定の共通ロジック
  const isHighlighted = useCallback(
    (statusData: StatusType) => {
      // ホバーした予約のcheckinIdと, 表示対象のcheckinIdが一致し
      if (
        statusData.checkinId &&
        statusData.checkinId === hoveredCheckinId &&
        // そのcheckinIdの予約が複数ある場合
        groupableAssignIds.checkinIds.has(statusData.checkinId)
      ) {
        return true
      }

      // ホバーした予約のreservationIdと, 表示対象のreservationIdが一致し
      if (
        statusData.reservationId &&
        statusData.reservationId === hoveredReservationId &&
        // そのreservationIdの予約が複数ある場合
        groupableAssignIds.reservationIds.has(statusData.reservationId)
      ) {
        return true
      }

      return false
    },
    [hoveredCheckinId, hoveredReservationId, groupableAssignIds],
  )

  return {
    setHoveredCheckinId,
    setHoveredReservationId,
    isHighlighted,
  }
}
